import { create } from 'zustand';
import type { AuthResponse, PendingVerification, UserDto } from '@timato/shared';
import { db } from '@/db/db';
import { deviceId as localDeviceId } from '@/lib/device';
import { api, bindTokenSource, refreshSession } from './api';
import { labelFromNavigator } from './deviceLabel';
import { resetSyncState } from '@/sync/state';

type Status = 'loading' | 'signedOut' | 'signedIn';
type AuthState = {
  status: Status; user: UserDto | null; accessToken: string | null; deviceId: string | null;
  /** When the access token lapses (epoch ms), so it can be renewed just before rather than after a 401. */
  expiresAt: number | null;
  /** The server ended this device's session while the app was open (TW-2). The shell says so once. */
  sessionLost: boolean;
  bootstrap: () => Promise<void>;
  /** Resolve to a pending step when the address still has to be confirmed; otherwise the session is set. */
  register: (email: string, password: string, name: string) => Promise<PendingVerification | undefined>;
  signIn: (email: string, password: string) => Promise<PendingVerification | undefined>;
  verifyEmail: (pendingToken: string, code: string) => Promise<void>;
  /** `revoke: false` when the server has already ended the session: forget it here, and ask nothing of the server. */
  signOut: (opts?: { clearLocal?: boolean; revoke?: boolean }) => Promise<void>;
  /** After the OAuth redirect: the cookie is set, one refresh signs this tab in. */
  completeOAuth: () => Promise<boolean>;
  setSession: (r: AuthResponse) => void;
  setUser: (u: UserDto) => void;
};

const META_KEY = 'auth';
/** The API answers a password sign-in with a session (200) or a pending step (202); only the first signs this tab in. */
const settle = (r: AuthResponse | PendingVerification) => { if ('pendingToken' in r) return r; useAuth.getState().setSession(r); return undefined; };
type Persisted = { user: UserDto; deviceId: string };
const deviceInfo = () => ({ label: labelFromNavigator(), platform: 'web' as const });

/**
 * Session state for the web client: access token in memory, refresh token in the HttpOnly cookie the API sets,
 * user + device id mirrored to Dexie meta so the shell can render signed-in state offline before the first refresh.
 */
export const useAuth = create<AuthState>((set, get) => ({
  status: 'loading', user: null, accessToken: null, deviceId: null, expiresAt: null, sessionLost: false,

  setSession(r) { set({ status: 'signedIn', user: r.user, accessToken: r.accessToken, deviceId: r.deviceId, expiresAt: Date.now() + r.expiresIn * 1000, sessionLost: false }); void db.meta.put({ key: META_KEY, value: { user: r.user, deviceId: r.deviceId } satisfies Persisted }); },
  setUser(user) { set({ user }); const d = get().deviceId; if (d) void db.meta.put({ key: META_KEY, value: { user, deviceId: d } satisfies Persisted }); },

  async bootstrap() {
    // Only for the unknown state at cold start. The shell mounts again after the auth card signs someone in, and a
    // refresh then would compete with the session it just received; where the cookie cannot travel it would even
    // win, and sign the person straight back out.
    if (get().status !== 'loading') return;
    const persisted = (await db.meta.get(META_KEY))?.value as Persisted | undefined;
    if (!persisted) { set({ status: 'signedOut' }); return; }
    set({ user: persisted.user, deviceId: persisted.deviceId }); // optimistic, offline-friendly
    const r = navigator.onLine ? await refreshSession() : null;
    if (r) get().setSession(r);
    else if (navigator.onLine) await get().signOut(); // cookie gone or device revoked
    else set({ status: 'signedIn' }); // offline: trust the mirror until we can check
  },

  async completeOAuth() { const r = await refreshSession(); if (r) get().setSession(r); else set({ status: 'signedOut' }); return !!r; },

  async register(email, password, name) { return settle(await api<AuthResponse | PendingVerification>('/v1/auth/register', { json: { email, password, name, device: deviceInfo() }, auth: false })); },
  async signIn(email, password) { return settle(await api<AuthResponse | PendingVerification>('/v1/auth/password', { json: { email, password, device: deviceInfo() }, auth: false })); },
  async verifyEmail(pendingToken, code) { get().setSession(await api<AuthResponse>('/v1/auth/verify', { json: { pendingToken, code, device: deviceInfo() }, auth: false })); },

  async signOut({ clearLocal = false, revoke = true } = {}) {
    if (revoke && get().accessToken) await api('/v1/auth/logout', { method: 'POST' }).catch(() => undefined);
    await db.meta.delete(META_KEY);
    await resetSyncState();
    if (clearLocal) await db.transaction('rw', [db.settings, db.activeTimer, db.labels, db.projects, db.tasks, db.sessions, db.outbox], async () => {
      await Promise.all([db.settings.clear(), db.activeTimer.clear(), db.labels.clear(), db.projects.clear(), db.tasks.clear(), db.sessions.clear(), db.outbox.clear()]);
    });
    set({ status: 'signedOut', user: null, accessToken: null, deviceId: null, expiresAt: null });
  },
}));

bindTokenSource({
  getAccessToken: () => useAuth.getState().accessToken,
  getExpiresAt: () => useAuth.getState().expiresAt,
  onRefreshed: (r) => useAuth.getState().setSession(r),
  // Refused mid-session: the session is already over on the server, so there is nothing to revoke. Calling /logout
  // anyway could only hurt, by ending a session another tab had just renewed. Local data and the outbox stay, and
  // upload once the person signs in again.
  onSignedOut: () => { if (useAuth.getState().status !== 'signedIn') return; useAuth.setState({ sessionLost: true }); void useAuth.getState().signOut({ revoke: false }); },
});

void localDeviceId; // the local id keys the HLC; the server-issued deviceId keys the session
